import React from 'react';

/**
 * Page body spacing, and the two containers that sit directly under a Header.
 *
 * The newer screens were already close to agreeing here — eleven used
 * `space-y-4 p-4`, four used `p-1` with a 3 or 4 gap — but "close to agreeing"
 * across a module boundary is exactly what makes two screens look like they
 * came from different products. `p-4` wins on count and on not letting cards
 * touch the viewport edge.
 */

export const PageBody = ({ children, className = '' }) => (
  <div className={`space-y-4 p-4 ${className}`}>{children}</div>
);

/**
 * The row of summary figures. Two-up on a phone, four-up at most: the tiles
 * are sized like the Company page's cards now, and seven of those across
 * (Labour Spend) squeezed the figures into wrapping. Wider rows wrap instead.
 */
export const TileRow = ({ children, className = '' }) => (
  <div className={`grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 ${className}`}>
    {children}
  </div>
);

/**
 * The filter/segmented-control strip. `end` is pinned right — date ranges and
 * Clear belong away from the grouping controls, which is the arrangement
 * Labour Spend arrived at and the one worth keeping.
 */
export const FilterBar = ({ children, end, className = '' }) => (
  <div
    className={`flex flex-wrap items-end gap-3 rounded-panel border border-hairline bg-surface p-3 ${className}`}>
    {children}
    {end && <div className="ml-auto flex items-end gap-2">{end}</div>}
  </div>
);

/** A titled white card for anything that is not a table. */
export const SectionCard = ({ title, subtitle, action, children, className = '' }) => (
  <div className={`rounded-panel border border-hairline bg-surface p-4 ${className}`}>
    {(title || action) && (
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          {title && <h3 className="text-sm font-medium text-gray-800">{title}</h3>}
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

/**
 * Nothing-here state for a whole panel.
 *
 * Dashed rather than solid: a solid hairline reads as a card that failed to
 * load, a dashed one reads as a space waiting to be filled.
 */
export const EmptyState = ({ title = 'Nothing to show.', hint, action, className = '' }) => (
  <div
    className={`rounded-panel border border-dashed border-hairline-strong bg-surface py-10 text-center ${className}`}>
    <p className="text-sm text-gray-500">{title}</p>
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);
